import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "hooks/useQuote";
import { useStickyBannerData } from "hooks/useStickyBannerData";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";
import { ROUTES } from "routes/constants";
import { useProductConfig, useProductContext } from "context/ProductContext";
import { formatRegisteredSymbol } from "utils/textFormatting";
import { useIsMobile } from "hooks";

const StickyBanner = () => {
    const navigate = useNavigate();
    const { storeQuote } = useQuote();
    const { shouldShow, pageId, quote, coverageAmount, mltaDescription, isInvitationPage, isMLTAQuotePage } =
        useStickyBannerData();
    const config = useProductConfig();
    const isMobile = useIsMobile();
    const { isMLTA } = useProductContext();

    if (!shouldShow || !pageId) {
        return null;
    }

    const hasQuote = quote !== "-";
    const displayQuote = quote ?? "-";

    const getButtonText = () => {
        if (isInvitationPage) return "Get Started";
        if (!hasQuote || isMLTAQuotePage) return "Get Quote";
        return "Apply Now";
    };

    const buttonText = getButtonText();
    const shouldScrollToTop = buttonText === "Get Quote" || buttonText === "Get Started";

    const handleButtonClick = () => {
        if (shouldScrollToTop) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            storeQuote();
            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_APPLY_NOW);
            navigate(ROUTES.APPLICATION.CONTACT);
        }
    };

    const renderBannerContent = () => {
        if (isInvitationPage) {
            return (
                <div className="text-secondary font-bold text-lg leading-7 tracking-normal">
                    {isMobile ? config.mobileDisplayName : formatRegisteredSymbol(config.displayName)}
                </div>
            );
        }

        return (
            <div className="flex flex-col">
                <p className="text-secondary font-semibold text-xl leading-7 tracking-normal">${displayQuote}/month</p>
                {isMLTA ? (
                    mltaDescription && (
                        <p className="text-secondary text-xs tablet:text-base font-medium text-base leading-6 tracking-normal">
                            {formatRegisteredSymbol(mltaDescription)}
                        </p>
                    )
                ) : (
                    <p className="text-secondary font-medium text-base leading-6 tracking-normal">
                        ${coverageAmount} Term Life Insurance
                    </p>
                )}
            </div>
        );
    };

    return (
        <div
            className={`w-full bg-warning-content rounded-b-xl tablet:rounded-b-2xl
            px-4 tablet:px-8 py-2
            flex justify-between items-center
            mb-[-10px]
            sticky top-0 z-20 ${isInvitationPage ? "h-[52px]" : "h-[76px]"}`}
        >
            {renderBannerContent()}
            <button
                onClick={handleButtonClick}
                className="bg-white text-primary font-semibold text-sm leading-5 tracking-normal text-center
                    w-[102px] h-[36px] min-h-[24px] max-h-[64px] rounded flex items-center justify-center
                    hover:bg-gray-100 transition-colors"
            >
                {buttonText}
            </button>
        </div>
    );
};

export default StickyBanner;
